import React from 'react'

import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from 'recharts'

const formatNumber = (value, prefix = '') => {
	if (value === null || value === undefined) return '0'

	if (value >= 1000000) {
		return `${prefix}${(value / 1000000).toFixed(1)}M`
	}
	if (value >= 1000) {
		return `${prefix}${(value / 1000).toFixed(1)}K`
	}
	return `${prefix}${value.toFixed(1)}`
}

// Custom tooltip similar to TrendCombo
const CustomTooltip = ({ active, payload, isDarkMode, getGradeColor }) => {
	if (!active || !payload || !payload.length) return null
	const d = payload[0].payload
	const bg = isDarkMode ? '#2d333b' : '#ffffff'
	const txt = isDarkMode ? '#ffffff' : '#111827'

	const borderColor = isDarkMode ? '#444c56' : 'none'
	const gradeColor = getGradeColor(d.name)

	return (
		<div
			style={{
				backgroundColor: bg,
				border: isDarkMode ? `1px solid ${borderColor}` : 'none',
				borderRadius: '12px',
				boxShadow:
					'0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
				color: txt,
				padding: '16px',
				minWidth: '220px',
			}}
		>
			<div
				style={{
					color: gradeColor,
					fontSize: '14px',
					fontWeight: '500',
					marginBottom: '8px',
				}}
			>
				{d.name}
			</div>
			{/* Data rows */}
			<div style={{ fontSize: '13px', padding: '4px 0', color: txt }}>
				<span style={{ fontWeight: '500' }}>Grade can book </span>{' '}
				{Math.round(d.coveragePct)}% of all Amazon loads
			</div>
			<div style={{ fontSize: '13px', padding: '4px 0', color: txt }}>
				<span style={{ fontWeight: '500' }}>Loads:</span>{' '}
				{formatNumber(d.loads)}
			</div>
			<div style={{ fontSize: '13px', padding: '4px 0', color: txt }}>
				<span style={{ fontWeight: '500' }}>Revenue:</span>{' '}
				{formatNumber(d.revenue, '$')}
			</div>
			<div style={{ fontSize: '13px', padding: '4px 0', color: txt }}>
				<span style={{ fontWeight: '500' }}>Avg RPM:</span> $
				{d.rpm?.toFixed(2) || '0.00'}
			</div>
		</div>
	)
}

export default function PerformanceGauge({ pct, isDarkMode }) {
	const breakdown = pct?.breakdown || []

	// Rank helper (needs to be defined before usage)
	const getGradeRank = grade => {
		const rankMap = {
			'A+': 8,
			A: 7,
			'B+': 6,
			B: 5,
			'C+': 4,
			C: 3,
			'D+': 2,
			D: 1,
			Others: 0,
		}
		return rankMap[grade] || 0
	}

	// Get color for a grade
	const getGradeColor = grade => {
		switch (grade) {
			case 'A+':
				return isDarkMode ? '#4ade80' : '#10b981' // emerald
			case 'A':
				return isDarkMode ? '#60a5fa' : '#2563eb' // blue
			case 'B+':
				return isDarkMode ? '#f472b6' : '#db2777' // pink
			case 'B':
				return isDarkMode ? '#c084fc' : '#9333ea' // purple
			case 'C+':
				return isDarkMode ? '#fb923c' : '#ea580c' // orange
			case 'C':
				return isDarkMode ? '#fbbf24' : '#d97706' // amber
			case 'D+':
				return isDarkMode ? '#f87171' : '#dc2626' // red
			case 'D':
				return isDarkMode ? '#ef4444' : '#b91c1c' // dark red
			default:
				return isDarkMode ? '#94a3b8' : '#64748b' // gray
		}
	}

	// Normalize grades: keep A+, A, B+, B, else aggregate into Others
	const allowedGrades = ['A+', 'A', 'B+', 'B']

	// First map to allowed or Others
	const mapped = breakdown
		.filter(g => g.grade !== 'Ungraded')
		.map(g => ({
			...g,
			grade: allowedGrades.includes(g.grade) ? g.grade : 'Others',
		}))

	// Aggregate duplicates (e.g., multiple Others sources)
	const aggMap = {}
	for (const g of mapped) {
		if (!aggMap[g.grade]) {
			aggMap[g.grade] = { ...g }
		} else {
			aggMap[g.grade].count += g.count
			aggMap[g.grade].revenue += g.revenue
			aggMap[g.grade].accessible += g.accessible || 0
			// avgPpm: recalc weighted based on revenue/distance if distance present else leave
		}
	}

	const sortedBreakdown = Object.values(aggMap).sort(
		(a, b) => getGradeRank(b.grade) - getGradeRank(a.grade)
	)

	// Calculate total loads (excluding Ungraded)
	const totalLoads = sortedBreakdown.reduce(
		(sum, grade) => sum + grade.count,
		0
	)

	// Pie chart sizing
	const PIE_INNER = 60
	const PIE_OUTER = 80

	// Card styling variables similar to TrendCombo
	const backgroundColor = isDarkMode ? '#2d333b' : '#ffffff'
	const borderColor = isDarkMode ? '#444c56' : '#cccccc'

	/* -------- Full breakdown for chart -------- */
	const fullBreakdownSorted = [...breakdown]
		.filter(g => g.grade !== 'Ungraded')
		.map(g => ({
			...g,
			grade: g.grade === '<B' ? 'Others' : g.grade,
		}))
		.sort((a, b) => getGradeRank(b.grade) - getGradeRank(a.grade))

	const fullDisplayBreakdown = fullBreakdownSorted.map(g => ({
		...g,
		displayPercent: Math.round((g.count / totalLoads) * 100),
	}))

	// Pie slices sized by accessible loads (cumulative coverage)
	const pieData = fullDisplayBreakdown
		.map(g => ({
			name: g.grade,
			value: g.accessible ?? g.count,
			loads: g.accessible ?? g.count,
			revenue: g.revenue,
			rpm: g.avgPpm,
			coveragePct: g.coveragePct ?? g.displayPercent,
		}))
		.filter(d => d.value > 0)

	/* -------- Aggregated breakdown for text -------- */
	const aggMapText = {}
	for (const g of fullDisplayBreakdown) {
		const key = allowedGrades.includes(g.grade) ? g.grade : 'Others'
		if (!aggMapText[key]) {
			aggMapText[key] = { ...g, grade: key }
		} else {
			aggMapText[key].count += g.count
			aggMapText[key].accessible =
				(aggMapText[key].accessible || 0) + (g.accessible || 0)
			aggMapText[key].revenue += g.revenue
		}
	}

	const textBreakdown = Object.values(aggMapText)
		.map(item => ({
			...item,
			displayPercent: Math.round((item.count / totalLoads) * 100),
			coveragePct: Math.round(item.coveragePct),
		}))
		.sort((a, b) => getGradeRank(b.grade) - getGradeRank(a.grade))

	// Custom label that renders outside the pie
	const renderCustomizedLabel = ({
		cx,
		cy,
		midAngle,
		innerRadius,
		outerRadius,
		percent,
		index,
		name,
		value,
	}) => {
		const RADIAN = Math.PI / 180
		const radius = outerRadius + 20
		const x = cx + radius * Math.cos(-midAngle * RADIAN)
		const y = cy + radius * Math.sin(-midAngle * RADIAN)

		// Use displayPercent from displayBreakdown
		const gradeInfo = fullDisplayBreakdown.find(g => g.grade === name)
		const percentage = `${Math.round(gradeInfo?.coveragePct ?? 0)}%`

		return (
			<text
				x={x}
				y={y}
				fill={getGradeColor(name)}
				textAnchor={x > cx ? 'start' : 'end'}
				dominantBaseline="central"
				style={{ fontWeight: 600, fontSize: 12 }}
			>
				{name} {percentage}
			</text>
		)
	}

	return (
		<div
			className="rounded-lg p-4"
			style={{
				backgroundColor,
				boxShadow: isDarkMode
					? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)'
					: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
				border: `1px solid ${borderColor}`,
			}}
		>
			<h3
				className={`text-md font-medium mb-2 ${
					isDarkMode ? 'text-white' : 'text-gray-800'
				}`}
			>
				Load Distribution by Performance Grade
			</h3>
			<div className="flex flex-col items-center">
				<div className="w-full max-w-2xl h-96">
					<ResponsiveContainer width="100%" height={320}>
						<PieChart margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
							<Pie
								data={pieData}
								innerRadius={PIE_INNER + 55}
								outerRadius={PIE_OUTER + 55}
								startAngle={90}
								endAngle={-270}
								dataKey="value"
								label={renderCustomizedLabel}
								labelLine={false}
							>
								{pieData.map((entry, idx) => (
									<Cell key={entry.name} fill={getGradeColor(entry.name)} />
								))}
								<text
									x="50%"
									y="50%"
									textAnchor="middle"
									dominantBaseline="central"
									className={`text-sm font-semibold ${
										isDarkMode ? 'fill-white' : 'fill-gray-800'
									}`}
								>
									{pieData.length} Grades
								</text>
							</Pie>
							<Tooltip
								content={
									<CustomTooltip
										isDarkMode={isDarkMode}
										getGradeColor={getGradeColor}
									/>
								}
								wrapperStyle={{ outline: 'none' }}
							/>
						</PieChart>
					</ResponsiveContainer>
				</div>
				<div className="w-full mt-4 flex flex-col">
					<div className="grid grid-cols-5 gap-x-4 gap-y-2">
						{/* Header row */}
						<div
							className={`text-xs font-semibold ${
								isDarkMode ? 'text-gray-300' : 'text-gray-600'
							}`}
						>
							Grade
						</div>
						<div
							className={`text-xs font-semibold ${
								isDarkMode ? 'text-gray-300' : 'text-gray-600'
							}`}
						>
							Coverage
						</div>
						<div
							className={`text-xs font-semibold ${
								isDarkMode ? 'text-gray-300' : 'text-gray-600'
							}`}
						>
							Loads
						</div>
						<div
							className={`text-xs font-semibold ${
								isDarkMode ? 'text-gray-300' : 'text-gray-600'
							}`}
						>
							Revenue
						</div>
						<div
							className={`text-xs font-semibold ${
								isDarkMode ? 'text-gray-300' : 'text-gray-600'
							}`}
						>
							RPM
						</div>

						{/* Data rows */}
						{textBreakdown.map(grade => (
							<React.Fragment key={grade.grade}>
								<div
									className="font-medium text-sm"
									style={{ color: getGradeColor(grade.grade) }}
								>
									{grade.grade}
								</div>
								<div
									className={`font-medium text-sm ${
										isDarkMode ? 'text-gray-300' : 'text-gray-600'
									}`}
								>
									{Math.round(grade.coveragePct)}%
								</div>
								<div
									className={`font-medium text-sm ${
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									}`}
								>
									{formatNumber(grade.accessible ?? grade.count)}
								</div>
								<div
									className={`font-medium text-sm ${
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									}`}
								>
									{formatNumber(grade.revenue, '$')}
								</div>
								<div
									className={`font-medium text-sm ${
										isDarkMode ? 'text-gray-400' : 'text-gray-500'
									}`}
								>
									${grade.avgPpm?.toFixed(2) || '0.00'}
								</div>
							</React.Fragment>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}
